 import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import PopupChart from '../PopupChart/PopupChart';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsCharts: React.FC = () => {
  const { 
    funds, 
    performances, 
    riskIndicators, 
    reportData, 
    loading, 
    refreshReportData, 
    refreshRiskIndicators,
    refreshWeightedRIByDate,
    refreshPortfolioWeightedRisk,
    strategies,
    styles
  } = useHedgeFundData();
  
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Filter state
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [dateRange, setDateRange] = useState<string>('3M');
  const [selectedStrategies, setSelectedStrategies] = useState<number[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<number[]>([]);
  
  // NEW: Portfolio-weighted risk analysis data
  const [portfolioWeightedData, setPortfolioWeightedData] = useState<any[]>([]);
  const [weightedRIData, setWeightedRIData] = useState<any[]>([]);
  const [loadingPortfolioAnalysis, setLoadingPortfolioAnalysis] = useState(false);

  // Popup chart state
  const [popupChartOpen, setPopupChartOpen] = useState(false);
  const [popupChartData, setPopupChartData] = useState<{
    fundId?: number;
    fieldId?: number;
    chartType?: string;
    title?: string;
  }>({});

  // Load data on initial mount - loads ALL tabs (Dashboard, Performance, Risk Analysis, Breach Reports)
  useEffect(() => {
    console.log('📊 Reports & Charts: Initial data load for all tabs');
    loadReportData();
  }, []); // Empty dependency array - only run once on mount

  const loadReportData = async () => {
    setError(null);
    setLoadingPortfolioAnalysis(true);
    
    try {
      console.log('🔄 Loading data for ALL report tabs with current configuration:', {
        selectedFund,
        dateRange,
        selectedStrategies,
        selectedStyles
      });
      
      // Calculate date range for analysis
      // For "All Time", don't set any dates so the query has no date filters
      let startDateStr: string | undefined = undefined;
      let endDateStr: string | undefined = undefined;
      
      if (dateRange !== 'ALL') {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (dateRange) {
          case '1M':
            startDate.setMonth(endDate.getMonth() - 1);
            break;
          case '3M':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case '6M':
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case '1Y':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
        }
        
        startDateStr = startDate.toISOString().split('T')[0];
        endDateStr = endDate.toISOString().split('T')[0];
        console.log('Using date range:', { startDateStr, endDateStr, dateRange });
      } else {
        console.log('All Time selected - no date filters will be applied');
      }
      
      // 1. Load Dashboard data (fund statistics and breach summary)
      console.log('📈 Loading Dashboard data...');
      const reportDataPromise = refreshReportData({
        selectedFund,
        dateRange
      });
      
      // 2. Load Performance data for Performance tab
      console.log('📊 Loading Performance data...');
      // Performance data is already in context via 'performances' prop
      // No explicit refresh needed as it's loaded globally
      
      // 3. Load Risk Analysis data
      console.log('🎯 Loading Risk Analysis data...');
      const riskIndicatorPromise = startDateStr && endDateStr 
        ? refreshRiskIndicators({
            start_date: startDateStr,
            end_date: endDateStr
          })
        : refreshRiskIndicators({});
      
      // Load portfolio-weighted risk analysis using uspWeightedRIByDate
      console.log('🚀 Loading portfolio-weighted risk analysis using uspWeightedRIByDate...');
      const weightedRIPromise = startDateStr && endDateStr
        ? refreshWeightedRIByDate(startDateStr, endDateStr)
        : Promise.resolve([]); // Skip if no dates for All Time
      
      // Load portfolio-weighted analysis with strategy/style filtering
      const portfolioWeightedPromise = startDateStr && endDateStr
        ? refreshPortfolioWeightedRisk({
            start_date: startDateStr,
            end_date: endDateStr,
            strategy_ids: selectedStrategies.length > 0 ? selectedStrategies : undefined,
            style_ids: selectedStyles.length > 0 ? selectedStyles : undefined
          })
        : Promise.resolve([]); // Skip if no dates for All Time
      
      // 4. Load Breach Reports data (included in reportData from refreshReportData)
      console.log('⚠️ Loading Breach Reports data (included in Dashboard data)...');
      
      // Execute all promises with timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Data loading timed out after 45 seconds')), 45000);
      });
      
      const results = await Promise.race([
        Promise.allSettled([
          reportDataPromise,
          riskIndicatorPromise,
          weightedRIPromise,
          portfolioWeightedPromise
        ]),
        timeoutPromise
      ]) as PromiseSettledResult<any>[];
      
      // Handle report data results
      if (results[0].status === 'fulfilled') {
        console.log('✅ Dashboard/Breach data loaded successfully');
      } else {
        console.error('❌ Dashboard/Breach data failed:', results[0].reason);
      }
      
      // Handle risk indicator results
      if (results[1].status === 'fulfilled') {
        console.log('✅ Risk indicators loaded successfully');
      } else {
        console.error('❌ Risk indicators failed:', results[1].reason);
      }
      
      // Handle weighted RI results
      if (results[2].status === 'fulfilled') {
        setWeightedRIData(results[2].value || []);
        console.log('✅ uspWeightedRIByDate loaded successfully:', results[2].value?.length || 0, 'records');
      } else {
        console.error('❌ uspWeightedRIByDate failed:', results[2].reason);
      }
      
      // Handle portfolio weighted results
      if (results[3].status === 'fulfilled') {
        setPortfolioWeightedData(results[3].value || []);
        console.log('✅ Portfolio-weighted analysis loaded successfully:', results[3].value?.length || 0, 'records');
      } else {
        console.error('❌ Portfolio-weighted analysis failed:', results[3].reason);
      }
      
      console.log('✅ All report data loaded successfully');
      
    } catch (err) {
      console.error('❌ Error loading report data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load report data');
    } finally {
      setLoadingPortfolioAnalysis(false);
    }
  };

  const generatePerformanceChart = () => {
    if (!performances.length) return null;

    try {
      let filteredPerformances = performances;
      
      // Filter by date range only if NOT "All Time"
      if (dateRange !== 'ALL') {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (dateRange) {
          case '1M':
            startDate.setMonth(endDate.getMonth() - 1);
            break;
          case '3M':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case '6M':
            startDate.setMonth(endDate.getMonth() - 6);
            break;
          case '1Y':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
        }
        
        // Filter by date range
        filteredPerformances = performances.filter(perf => {
          const perfDate = new Date(perf.PerfDate);
          return perfDate >= startDate && perfDate <= endDate;
        });
      }
      
      // Filter by selected fund if specified
      if (selectedFund) {
        filteredPerformances = filteredPerformances.filter(perf => 
          perf.BackstopID.toString() === selectedFund
        );
      }


      if (!filteredPerformances.length) return null;

      const fundNames = [...new Set(filteredPerformances.map(d => d.FundName))];
      const dates = [...new Set(filteredPerformances.map(d => d.PerfDate))].sort();

      const datasets = fundNames.map((fund, index) => {
        const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF', '#4BC0C0', '#FF6384', '#36A2EB'];
        const color = colors[index % colors.length];
        
        return {
          label: fund,
          data: dates.map(date => {
            const dataPoint = filteredPerformances.find(d => d.FundName === fund && d.PerfDate === date);
            return dataPoint ? dataPoint.PerfValue * 100 : null;
          }),
          borderColor: color,
          backgroundColor: color + '20',
          fill: false,
          tension: 0.1
        };
      });

      return {
        labels: dates.map(date => new Date(date).toLocaleDateString()),
        datasets
      };
    } catch (error) {
      console.error('Error generating performance chart:', error);
      return null;
    }
  };

  const generateRiskChart = () => {
    // STRICT: Only use portfolio-weighted risk analysis data from uspWeightedRIByDate
    // NO FALLBACK to fake data - fail if stored procedure data is not available
    if (weightedRIData.length > 0) {
      console.log('🎯 Using portfolio-weighted risk data from uspWeightedRIByDate:', weightedRIData.length, 'records');
      
      try {
        // Process weighted RI data for time series chart
        const dateMap = new Map();
        
        weightedRIData.forEach(record => {
          const date = record.MonitorDate || record.Date;
          const riValue = record.WeightedRI || record.RIValue || 0;
          const fundName = record.FundName || 'Portfolio Weighted';
          
          if (!dateMap.has(date)) {
            dateMap.set(date, {});
          }
          
          dateMap.get(date)[fundName] = riValue;
        });
        
        const dates = Array.from(dateMap.keys()).sort();
        const fundNames = [...new Set(weightedRIData.map(r => r.FundName || 'Portfolio Weighted'))];
        
        const datasets = fundNames.map((fund, index) => {
          const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF', '#4BC0C0', '#FF6384', '#36A2EB'];
          const color = colors[index % colors.length];
          
          return {
            label: `${fund} - Weighted RI`,
            data: dates.map(date => {
              const dateData = dateMap.get(date);
              return dateData && dateData[fund] ? dateData[fund] : null;
            }),
            borderColor: color,
            backgroundColor: color + '20',
            fill: false,
            tension: 0.1
          };
        });

        return {
          labels: dates.map(date => new Date(date).toLocaleDateString()),
          datasets
        };
        
      } catch (error) {
        console.error('Error processing weighted RI data from uspWeightedRIByDate:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        throw new Error(`Failed to process portfolio-weighted risk data: ${errorMessage}`);
      }
    }
    
    // STRICT: No fallback data - return null if uspWeightedRIByDate data is not available
    console.warn('❌ No portfolio-weighted risk data available from uspWeightedRIByDate stored procedure');
    return null;
  };

  const generateFundDistribution = () => {
    if (!funds.length) return null;

    // Group funds by strategy
    const strategyGroups = funds.reduce((acc, fund) => {
      const strategy = fund.Strategy || 'Unknown';
      acc[strategy] = (acc[strategy] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      labels: Object.keys(strategyGroups),
      datasets: [{
        data: Object.values(strategyGroups),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40'
        ]
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return (value * 100).toFixed(2) + '%';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const exportToCSV = () => {
    let csvContent = '';
    let filename = '';

    switch (tabValue) {
      case 0: // Dashboard
        // Export fund statistics and breach summary
        csvContent = 'Fund Statistics\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Funds,${reportData.fundStats?.totalFunds || 0}\n`;
        csvContent += `Total AUM,${reportData.fundStats?.totalAUM || 0}\n`;
        csvContent += `Average Performance,${reportData.fundStats?.avgPerformance || 0}\n`;
        csvContent += `Risk Breaches,${reportData.fundStats?.riskBreaches || 0}\n\n`;
        
        csvContent += 'Breach Summary\n';
        csvContent += 'Fund,Indicator,Value,Threshold,Severity,Breach Percent,Date\n';
        reportData.breachSummary.forEach(breach => {
          csvContent += `${breach.fund},${breach.indicator},${breach.value},${breach.threshold},${breach.severity},${breach.breachPercent.toFixed(2)},${breach.date}\n`;
        });
        filename = 'dashboard_report.csv';
        break;

      case 1: // Performance
        csvContent = 'Performance Data\n';
        csvContent += 'Fund,Date,Performance Value,Currency,Quality,Type\n';
        performances.forEach(perf => {
          csvContent += `${perf.FundName},${perf.PerfDate},${perf.PerfValue},${perf.Currency},${perf.PerfQualityName},${perf.PerfTypeName}\n`;
        });
        filename = 'performance_data.csv';
        break;

      case 2: // Risk Analysis
        csvContent = 'Risk Indicators\n';
        csvContent += 'Fund,Strategy,Style,Date,Risk Indicator,Value,Threshold,Is Breached\n';
        riskIndicators.forEach(risk => {
          csvContent += `${risk.FundName},${risk.StrategyName},${risk.StyleName},${risk.MonitorDate},${risk.RIName},${risk.RIValue},${risk.ThresholdValue},${risk.IsBreached}\n`;
        });
        filename = 'risk_analysis.csv';
        break;

      case 3: // Breach Reports
        csvContent = 'Threshold Breaches\n';
        csvContent += 'Fund,Indicator,Current Value,Threshold,Severity,Breach Percent,Date\n';
        reportData.breachSummary.forEach(breach => {
          csvContent += `${breach.fund},${breach.indicator},${breach.value},${breach.threshold},${breach.severity},${breach.breachPercent.toFixed(2)},${breach.date}\n`;
        });
        filename = 'breach_report.csv';
        break;
    }

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = async () => {
    // For PDF export, we'll create a simple HTML report and use the browser's print functionality
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let htmlContent = `
      <html>
        <head>
          <title>Hedge Fund Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1, h2 { color: #1a237e; }
            table { border-collapse: collapse; width: 100%; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .metric-card { display: inline-block; margin: 10px; padding: 15px; border: 1px solid #ddd; border-radius: 5px; text-align: center; }
            .breach-high { color: #f44336; }
            .breach-medium { color: #ff9800; }
            .breach-low { color: #2196f3; }
          </style>
        </head>
        <body>
          <h1>Hedge Fund Database Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
    `;

    switch (tabValue) {
      case 0: // Dashboard
        htmlContent += `
          <h2>Key Metrics</h2>
          <div class="metric-card">
            <h3>${reportData.fundStats?.totalFunds || 0}</h3>
            <p>Total Funds</p>
          </div>
          <div class="metric-card">
            <h3>${formatCurrency(reportData.fundStats?.totalAUM || 0)}</h3>
            <p>Total AUM</p>
          </div>
          <div class="metric-card">
            <h3>${formatPercentage(reportData.fundStats?.avgPerformance || 0)}</h3>
            <p>Avg Performance</p>
          </div>
          <div class="metric-card">
            <h3>${reportData.fundStats?.riskBreaches || 0}</h3>
            <p>Risk Breaches</p>
          </div>
          
          <h2>Recent Breaches</h2>
          <table>
            <tr><th>Fund</th><th>Indicator</th><th>Value</th><th>Threshold</th><th>Severity</th><th>Breach %</th></tr>
        `;
        reportData.breachSummary.forEach(breach => {
          htmlContent += `
            <tr>
              <td>${breach.fund}</td>
              <td>${breach.indicator}</td>
              <td>${breach.value}</td>
              <td>${breach.threshold}</td>
              <td class="breach-${breach.severity.toLowerCase()}">${breach.severity}</td>
              <td>${breach.breachPercent.toFixed(2)}%</td>
            </tr>
          `;
        });
        htmlContent += '</table>';
        break;

      case 1: // Performance
        htmlContent += `
          <h2>Performance Data</h2>
          <table>
            <tr><th>Fund</th><th>Date</th><th>Performance</th><th>Currency</th><th>Quality</th></tr>
        `;
        performances.slice(0, 50).forEach(perf => { // Limit to 50 for PDF
          htmlContent += `
            <tr>
              <td>${perf.FundName}</td>
              <td>${perf.PerfDate}</td>
              <td>${formatPercentage(perf.PerfValue)}</td>
              <td>${perf.Currency}</td>
              <td>${perf.PerfQualityName}</td>
            </tr>
          `;
        });
        htmlContent += '</table>';
        break;

      case 2: // Risk Analysis
        htmlContent += `
          <h2>Risk Indicators</h2>
          <table>
            <tr><th>Fund</th><th>Strategy</th><th>Risk Indicator</th><th>Value</th><th>Threshold</th><th>Breached</th></tr>
        `;
        riskIndicators.slice(0, 50).forEach(risk => { // Limit to 50 for PDF
          htmlContent += `
            <tr>
              <td>${risk.FundName}</td>
              <td>${risk.StrategyName}</td>
              <td>${risk.RIName}</td>
              <td>${risk.RIValue}</td>
              <td>${risk.ThresholdValue}</td>
              <td>${risk.IsBreached ? 'Yes' : 'No'}</td>
            </tr>
          `;
        });
        htmlContent += '</table>';
        break;

      case 3: // Breach Reports
        htmlContent += `
          <h2>Threshold Breaches</h2>
          <table>
            <tr><th>Fund</th><th>Indicator</th><th>Current Value</th><th>Threshold</th><th>Severity</th><th>Breach %</th></tr>
        `;
        reportData.breachSummary.forEach(breach => {
          htmlContent += `
            <tr>
              <td>${breach.fund}</td>
              <td>${breach.indicator}</td>
              <td>${breach.value}</td>
              <td>${breach.threshold}</td>
              <td class="breach-${breach.severity.toLowerCase()}">${breach.severity}</td>
              <td>${breach.breachPercent.toFixed(2)}%</td>
            </tr>
          `;
        });
        htmlContent += '</table>';
        break;
    }

    htmlContent += `
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait a moment for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleExport = () => {
    // For now, default to CSV export. Could add a dropdown menu for format selection
    exportToCSV();
  };

  // Popup chart handlers
  const openPopupChart = (fundId?: number, fieldId?: number, chartType: string = 'line', title?: string) => {
    setPopupChartData({
      fundId,
      fieldId,
      chartType,
      title
    });
    setPopupChartOpen(true);
  };

  const closePopupChart = () => {
    setPopupChartOpen(false);
    setPopupChartData({});
  };

  return (
    <Box sx={{ mt: -4 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold', color: '#1a237e' }}>
        Reports & Charts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6">
            Report Configuration
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {loading.reportData && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Refreshing data...
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadReportData}
              disabled={loading.reportData || loading.riskIndicators}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              sx={{ backgroundColor: '#1a237e' }}
            >
              Export
            </Button>
          </Box>
        </Box>
        
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Fund Filter</InputLabel>
              <Select
                value={selectedFund}
                onChange={(e) => setSelectedFund(e.target.value)}
                label="Fund Filter"
              >
                <MenuItem value="">All Funds</MenuItem>
                {funds.map(fund => (
                  <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                    {fund.FundName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                label="Date Range"
              >
                <MenuItem value="1M">Last Month</MenuItem>
                <MenuItem value="3M">Last 3 Months</MenuItem>
                <MenuItem value="6M">Last 6 Months</MenuItem>
                <MenuItem value="1Y">Last Year</MenuItem>
                <MenuItem value="ALL">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Strategy Filter</InputLabel>
              <Select
                multiple
                value={selectedStrategies}
                onChange={(e) => setSelectedStrategies(e.target.value as number[])}
                label="Strategy Filter"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const strategy = strategies.find(s => s.StrategyID === value);
                      return (
                        <Chip key={value} label={strategy?.StrategyName || value} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {strategies.map(strategy => (
                  <MenuItem key={strategy.StrategyID} value={strategy.StrategyID}>
                    {strategy.StrategyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Style Filter</InputLabel>
              <Select
                multiple
                value={selectedStyles}
                onChange={(e) => setSelectedStyles(e.target.value as number[])}
                label="Style Filter"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const style = styles.find(s => s.StyleID === value);
                      return (
                        <Chip key={value} label={style?.StyleName || value} size="small" />
                      );
                    })}
                  </Box>
                )}
              >
                {styles.map(style => (
                  <MenuItem key={style.StyleID} value={style.StyleID}>
                    {style.StyleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation Tabs */}
      <Paper 
        elevation={0}
        sx={{ 
          mb: 2,
          backgroundColor: '#f5f5f5'
        }}
      >
        <Box sx={{ px: 2, pt: 0.5 }}>
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            aria-label="reports tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontSize: '0.8125rem',
                fontWeight: 500,
                minWidth: 100,
                padding: '6px 12px'
              },
              '& .MuiTab-iconWrapper': {
                fontSize: '1rem'
              }
            }}
          >
            <Tab icon={<AssessmentIcon />} label="Dashboard" />
            <Tab icon={<TimelineIcon />} label="Performance" />
            <Tab icon={<BarChartIcon />} label="Risk Analysis" />
            <Tab icon={<WarningIcon />} label="Breach Reports" />
          </Tabs>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
      </Paper>

      {/* Dashboard Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Key Metrics
              </Typography>
              {loading.reportData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading statistics...
                  </Typography>
                </Box>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <BusinessIcon sx={{ fontSize: 40, color: '#1a237e', mb: 1 }} />
                    {loading.reportData ? (
                      <CircularProgress size={24} sx={{ my: 1 }} />
                    ) : (
                      <Typography variant="h4">{reportData.fundStats?.totalFunds || 0}</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Total Funds
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                    {loading.reportData ? (
                      <CircularProgress size={24} sx={{ my: 1 }} />
                    ) : (
                      <Typography variant="h4">
                        {reportData.fundStats ? formatCurrency(reportData.fundStats.totalAUM) : '$0'}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Total AUM
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                    {loading.reportData ? (
                      <CircularProgress size={24} sx={{ my: 1 }} />
                    ) : (
                      <Typography variant="h4">
                        {reportData.fundStats ? formatPercentage(reportData.fundStats.avgPerformance) : '0%'}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Avg Performance
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
                    {loading.reportData ? (
                      <CircularProgress size={24} sx={{ my: 1 }} />
                    ) : (
                      <Typography variant="h4">{reportData.fundStats?.riskBreaches || 0}</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Risk Breaches
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Fund Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Fund Distribution by Strategy
                </Typography>
                {generateFundDistribution() && (
                  <Box sx={{ height: 300 }}>
                    <Pie data={generateFundDistribution()!} options={{ responsive: true, maintainAspectRatio: false }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Breaches */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Recent Risk Breaches
                </Typography>
                {loading.reportData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <List>
                    {reportData.breachSummary.slice(0, 5).map((breach, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <WarningIcon color={getSeverityColor(breach.severity) as any} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${breach.fund} - ${breach.indicator}`}
                            secondary={
                              <React.Fragment>
                                <Chip
                                  label={breach.severity}
                                  size="small"
                                  color={getSeverityColor(breach.severity) as any}
                                  sx={{ mr: 1, mt: 0.5 }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>
                                  Value: {breach.value} | Threshold: {breach.threshold}
                                </span>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                        {index < reportData.breachSummary.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                    {reportData.breachSummary.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                        No recent breaches found
                      </Typography>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Performance Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Performance Trends
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {loading.performances && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading performance data...
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => openPopupChart(
                    selectedFund ? parseInt(selectedFund) : undefined,
                    undefined,
                    'line',
                    'Performance Analysis'
                  )}
                  disabled={!generatePerformanceChart()}
                >
                  Popup Chart
                </Button>
              </Box>
            </Box>
            {loading.performances ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, height: 400 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading performance trends...
                </Typography>
              </Box>
            ) : generatePerformanceChart() ? (
              <Box sx={{ height: 400 }}>
                <Line data={generatePerformanceChart()!} options={{ ...chartOptions, maintainAspectRatio: false }} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                No performance data available for the selected filters
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Risk Analysis Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Portfolio-Weighted Risk Analysis
              </Typography>
              {(loading.riskIndicators || loadingPortfolioAnalysis) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {loadingPortfolioAnalysis ? 'Loading portfolio-weighted analysis...' : 'Loading risk data...'}
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Show portfolio analysis status */}
            {weightedRIData.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>Using Portfolio-Weighted Risk Analysis:</strong> Data from uspWeightedRIByDate stored procedure ({weightedRIData.length} records)
              </Alert>
            )}
            
            {(loading.riskIndicators || loadingPortfolioAnalysis) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, height: 400 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {loadingPortfolioAnalysis ? 'Executing uspWeightedRIByDate stored procedure...' : 'Analyzing risk indicators...'}
                </Typography>
                {loadingPortfolioAnalysis && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    This may take a moment as we calculate portfolio-weighted risk metrics
                  </Typography>
                )}
              </Box>
            ) : generateRiskChart() ? (
              <Box sx={{ height: 400 }}>
                <Line data={generateRiskChart()!} options={{ ...chartOptions, maintainAspectRatio: false }} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                No risk data available for the selected filters
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Breach Reports Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Threshold Breach Analysis
              </Typography>
              {loading.reportData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading breach data...
                  </Typography>
                </Box>
              )}
            </Box>
            {loading.reportData ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, minHeight: 200 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Analyzing threshold breaches...
                </Typography>
              </Box>
            ) : reportData.breachSummary.length > 0 ? (
              <Grid container spacing={2}>
                {reportData.breachSummary.map((breach, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6">{breach.fund}</Typography>
                          <Chip
                            label={breach.severity}
                            size="small"
                            color={getSeverityColor(breach.severity) as any}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Risk Indicator: {breach.indicator}
                        </Typography>
                        <Typography variant="body1">
                          Current Value: <strong>{breach.value}</strong>
                        </Typography>
                        <Typography variant="body1">
                          Threshold: <strong>{breach.threshold}</strong>
                        </Typography>
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          Breach: {breach.breachPercent.toFixed(1)}% over threshold
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                No threshold breaches found
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Popup Chart Dialog */}
      <PopupChart
        open={popupChartOpen}
        onClose={closePopupChart}
        initialData={popupChartData}
      />
    </Box>
  );
};

export default ReportsCharts;
